import React, { useEffect, useState } from 'react';
import { Grid, TextField, Button, Checkbox, Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dialogStyles from '../../../../components/dialog/GenericDialog.styles';
import ValidatedField from '../../../../components/inputs/ValidatedField';
import useAuthUser from '../../../auth/hooks/useAuthUser';
import { formatName, generateFileName, toInputDate } from '../../utils/unit.helpers';
import { modifyUnit } from '../../services/unit.api';
import { UNIT_DATE_RULES } from '../../utils/unitValidation';

export default function ModifyUnitTab({ initialData, onSubmit, helpers }) {
  const { close, setLoading } = helpers;
  const [newEffectiveFrom, setNewEffectiveFrom] = useState('');
  const [stoodDown, setStoodDown] = useState(false);
  const navigate = useNavigate();

  const { user } = useAuthUser();
  const displayUser = formatName(user?.name) || user?.email || 'Unknown User';

  useEffect(() => {
    setNewEffectiveFrom('');
    setStoodDown(!!initialData?.stoodDown);
    setLoading(false);
  }, [setLoading, initialData?.name, initialData?.stoodDown]);

  const validDate = UNIT_DATE_RULES.every((r) => r.test(newEffectiveFrom));
  const canSave = Boolean(newEffectiveFrom) && validDate;

  const handleSave = async () => {
    if (!canSave) return;
    setLoading(true);
    const filename = generateFileName();
    try {
      await modifyUnit({
        unitName: initialData.name,
        effectiveFrom: newEffectiveFrom,
        stoodDown: stoodDown ? '1' : '0',
        user: displayUser,
        filename,
      });
      onSubmit?.();
      close();
      navigate('/home/structure-logs', { state: { fileName: filename, status: null } });
    } catch {
      alert('Failed to modify unit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Operator"
            fullWidth
            size="small"
            variant="standard"
            value={initialData.operator}
            InputProps={{ readOnly: true, disableUnderline: true }}
            InputLabelProps={{ shrink: true }}
            sx={dialogStyles.readonlyField}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Unit Name"
            fullWidth
            size="small"
            variant="standard"
            value={initialData.name}
            InputProps={{ readOnly: true, disableUnderline: true }}
            InputLabelProps={{ shrink: true }}
            sx={dialogStyles.readonlyField}
          />
        </Grid>
        <Grid item xs={12}>
          <Box>
            <Typography sx={{ fontSize: 12, color: '#555', mb: 0.5 }}>Stood Down?</Typography>
            <Checkbox
              checked={stoodDown}
              onChange={(e) => setStoodDown(e.target.checked)}
              size="small"
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Old Effective From"
            fullWidth
            size="small"
            type="date"
            variant="standard"
            value={toInputDate(initialData?.effectiveFrom)}
            InputProps={{ readOnly: true, disableUnderline: true }}
            InputLabelProps={{ shrink: true }}
            sx={dialogStyles.readonlyField}
          />
        </Grid>
        <Grid item xs={12}>
          <ValidatedField
            label="New Effective From"
            type="date"
            value={newEffectiveFrom}
            onChange={(e) => setNewEffectiveFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            rules={UNIT_DATE_RULES}
          />
        </Grid>
      </Grid>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
        <Button onClick={close} variant="contained" sx={dialogStyles.btnSecondary}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!canSave}
          variant="contained"
          sx={dialogStyles.btnPrimary}
        >
          Save Changes
        </Button>
      </div>
    </>
  );
}